import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Button, 
  Grid, 
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Chip,
  Tooltip,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Autocomplete,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { format, parseISO, isValid } from 'date-fns';
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useReportsHubBack } from '../../reports/hub/useReportsHubBack';
import FilterListIcon from '@mui/icons-material/FilterList';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import MoreVertIcon from '@mui/icons-material/MoreVert'; // ✅ 3 nokta ikonu
import DeleteIcon from '@mui/icons-material/Delete'; // ✅ Sil ikonu
import VisibilityIcon from '@mui/icons-material/Visibility'; // ✅ Filtreleri göster ikonu
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'; // ✅ Filtreleri gizle ikonu
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getSalesScreenOptions } from '../../../services/migration/salesScreenDataService';
import { useNavigate } from 'react-router-dom';
import BankTransactionDetailModal from './BankTransactionDetailModal';
import BankTransactionLogsModal from './BankTransactionLogsModal'; // ✅ Log Modal
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import ProjectBadge from '../shared/ProjectBadge';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';
import { getBankTxDirection, getBankTxSignedAmount, sumBankTxSigned, isBankTransferLeg, getBankTxLinkedSource } from './bankTxDirection';

// Banka işlemi arayüzü
interface BankTransaction {
  id: string;
  date: string;
  amount: number;
  bankAccountId: string;
  bankAccountName: string;
  bankName: string;
  bankSubAccountId?: string; // Alt hesap ID'si
  description: string;
  transactionType: string; // deposit, withdrawal, transfer, QUICK_SALE vb.
  type?: string; // income, expense - işlemin kasa/banka bakiyesine etkisi
  currency: string;
  relatedAccountId?: string;
  relatedAccountName?: string;
  documentNo?: string;
  createdAt: string; // Tam zaman damgası (ISO formatında olmalı)
  updatedAt: string;
  createdBy: string;
  paymentMethod?: string; // Ödeme yöntemi: cash, credit_card, bank_transfer, check, pos
  screenId?: string; // İşlemin yapıldığı satış ekranı ID'si
  screenName?: string; // İşlemin yapıldığı satış ekranı adı
  projectId?: number | null; // PYS: bağlı olduğu proje
  projectName?: string | null;
  referenceType?: string | null; // Bağlı kayıt türü (CHECK_NOTE, personnel_transaction, CREDIT_CARD_PAYMENT ...)
  transferGroupId?: string | null; // Transfer/virman çift kimliği (iki bacak birlikte silinir)
}

// Banka hesabı arayüzü
interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  currency: string;
}

// Tarih ve Saati Formatlama Fonksiyonu
const formatDateTime = (isoDateString: string, transactionType?: string): string => {
  if (!isoDateString) return '-';
  try {
    // Backend'den gelen tarih zaten UTC+3 formatında olduğu için direkt parse ediyoruz
    const date = parseISO(isoDateString);
    if (!isValid(date)) {
      return 'Invalid Date';
    }
    
    return format(date, 'dd.MM.yyyy HH:mm:ss');
  } catch (error) {
    console.error('Tarih/Saat formatlanırken hata:', error, 'Tarih:', isoDateString);
    return '-';
  }
};

// Tarih değerlerini ISO formatına dönüştürme
const convertToISODate = (firebaseDate: any): string => {
  if (!firebaseDate) return '';
  
  try {
    // Firebase Timestamp (seconds ve nanoseconds var)
    if (typeof firebaseDate === 'object' && firebaseDate.seconds && firebaseDate.nanoseconds) {
      return new Date(firebaseDate.seconds * 1000).toISOString();
    }
    
    // String formatındaki tarih
    if (typeof firebaseDate === 'string') {
      // Zaten ISO formatında ise
      if (isValid(parseISO(firebaseDate))) {
        return firebaseDate;
      }
      // Değilse dönüştürmeye çalış
      const date = new Date(firebaseDate);
      if (isValid(date)) {
        return date.toISOString();
      }
    }
    
    // Sayı değeri ise (timestamp)
    if (typeof firebaseDate === 'number') {
      return new Date(firebaseDate).toISOString();
    }
    
    return '';
  } catch (error) {
    console.error('Tarih dönüştürme hatası:', error);
    return '';
  }
};

const BankTransactionReport: React.FC = () => {
  const { t, i18n } = useTranslation('accounting');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const dateLocale = i18n.language?.toLowerCase().startsWith('en') ? enLocale : trLocale;

  // State tanımlamaları
  const [filteredTransactions, setFilteredTransactions] = useState<BankTransaction[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Filtre state'leri
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBankAccount, setSelectedBankAccount] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [transactionType, setTransactionType] = useState<string>('all');
  
  // Satış ekranları state'leri
  const [salesScreens, setSalesScreens] = useState<{ id: string; name: string; type: string }[]>([]);
  const [selectedSalesScreens, setSelectedSalesScreens] = useState<{ id: string; name: string; type: string }[]>([]);
  
  // Sıralama için state
  const [sortColumn, setSortColumn] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // İşlem detay modalı için state'ler
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<BankTransaction | null>(null);
  const [detailModalLoading, setDetailModalLoading] = useState(false);

  // Excel indirme için state
  const [loadingExport, setLoadingExport] = useState(false);

  // ✅ Silme menüsü için state'ler
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTransactionForDelete, setSelectedTransactionForDelete] = useState<BankTransaction | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // ✅ Log modalı için state
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

  // GENEL TOPLAM: sunucunun filtre geneli yönlü toplamı (para birimi bazında); yoksa açık sayfadan hesaplanır
  const [filterTotals, setFilterTotals] = useState<{ currency: string; total: number }[] | null>(null);

  // Filtre görünürlüğü için state (varsayılan kapalı)
  const [showFilters, setShowFilters] = useState(false);

  // Geri dönüş işlemi
  const { goBack: handleGoBack, fromHub } = useReportsHubBack('/bank/transactions', () => navigate('/banka'));

  // AccountingDataTable kolon tanımları
  const transactionColumns = useMemo<DataTableColumn<BankTransaction>[]>(() => [
    {
      id: 'rowNumber',
      label: t('bank.transactionReport.table.columns.row'),
      width: '60px',
      render: (_row, index) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>{page * rowsPerPage + index + 1}</Box>
      ),
    },
    {
      id: 'date',
      label: t('bank.transactionReport.table.columns.dateTime'),
      field: 'date',
      sortable: true,
      render: (row) => <>{formatDateTime(row.date, row.transactionType)}</>,
    },
    {
      id: 'bankName',
      label: t('bank.transactionReport.table.columns.bankAccount'),
      field: 'bankName',
      sortable: true,
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight="medium" component="span">
            {row.bankName}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="span">
            {' - '}{row.bankAccountName}
            {row.bankSubAccountId && ` (${t('bank.labels.subAccount')})`}
          </Typography>
          {/* Proje bağı hesap adının altında küçük etiket olarak gösterilir (PYS) */}
          <Box><ProjectBadge projectName={row.projectName} /></Box>
        </Box>
      ),
    },
    {
      id: 'transactionType',
      label: t('bank.transactionReport.table.columns.transactionType'),
      field: 'transactionType',
      sortable: true,
      render: (row) => {
        const typeInfo = getTransactionTypeInfo(row.transactionType);
        return (
          <Typography variant="body2" component="span" sx={{ fontWeight: 'medium', color: typeInfo.textColor }}>
            {typeInfo.label}
          </Typography>
        );
      },
    },
    {
      id: 'paymentMethod',
      label: t('bank.transactionReport.table.columns.paymentMethod'),
      field: 'paymentMethod',
      sortable: true,
      render: (row) => <>{row.paymentMethod ? getPaymentMethodName(row.paymentMethod) : '-'}</>,
    },
    {
      id: 'screenName',
      label: t('bank.transactionReport.table.columns.salesScreen'),
      field: 'screenName',
      sortable: true,
      render: (row) => <>{row.screenName || '-'}</>,
    },
    {
      id: 'description',
      label: t('bank.transactionReport.table.columns.description'),
      field: 'description',
      sortable: true,
      tooltip: true,
      render: (row) => (
        <Box>
          <Typography variant="body2" component="span" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
            {row.description}
          </Typography>
          {row.documentNo && (
            <Typography variant="caption" color="text.secondary" component="span">
              {` | ${t('bank.transactionReport.table.columns.documentNo')}: `}{row.documentNo}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'amount',
      label: t('bank.transactionReport.table.columns.amount'),
      field: 'amount',
      align: 'right',
      sortable: true,
      render: (row) => {
        // Yön: Type → [GİRİŞ]/[ÇIKIŞ] ön eki → transactionType (toplam ve Excel ile aynı kural)
        const isExpense = getBankTxDirection(row) < 0;
        return (
          <Typography variant="body2" fontWeight={isExpense ? 'bold' : 'medium'} color={isExpense ? 'error.main' : 'success.main'}>
            {row.amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(row.currency)}
          </Typography>
        );
      },
    },
    {
      id: 'actions',
      label: t('bank.transactionReport.table.columns.actions'),
      align: 'center',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title={t('bank.transactionReport.actions.detail')}>
            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleOpenDetailModal(row); }}>
              <RemoveRedEyeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('bank.transactionReport.table.columns.actions')}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, row); }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [locale, page, rowsPerPage, t]);
  
  // SQL API'den veri çekme
  useEffect(() => {
    loadCompanyAndData();
  }, []);
  
  const loadCompanyAndData = async () => {
    try {
      setLoading(true);
      
      // Kullanıcı oturum açmış mı kontrol et
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setLoading(false);
        setError(t('bank.transactionReport.errors.sessionNotFound'));
        return;
      }
      
      // Kullanıcının şirket ID'sini al (Cloud Functions)
      const userData = await firebaseDataService.getUserData(uid);
      
      if (!userData) {
        setLoading(false);
        setError(t('bank.transactionReport.errors.userNotFound'));
        return;
      }
      
      const companyId = userData.companyId;
      setCompanyId(companyId || null);
      
      if (!companyId) {
        setLoading(false);
        setError(t('bank.transactionReport.errors.companyNotFound'));
        return;
      }
      
      // Satış ekranlarını ve banka hesaplarını yükle
      await Promise.all([
        loadSalesScreens(companyId),
        loadBankAccounts(companyId)
      ]);
      
      setLoading(false);
      
    } catch (error) {
      console.error('Şirket veya veri yüklenirken hata:', error);
      setLoading(false);
      setError(t('bank.transactionReport.errors.loadFailed'));
    }
  };

  // Satış ekranlarını yükle (SQL — Hızlı Satış + PYK)
  const loadSalesScreens = async (companyId: string) => {
    try {
      const screensArray = await getSalesScreenOptions(companyId);
      console.log(`${screensArray.length} satış ekranı bulundu (SQL)`);
      setSalesScreens(screensArray);
    } catch (error) {
      console.error('Satış ekranları yüklenirken hata:', error);
    }
  };
  
  // Server-side sayfalama ile banka işlemlerini yükleme
  const loadTransactions = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);
      
      const bankAccId = selectedBankAccount !== 'all' ? parseInt(selectedBankAccount) : undefined;
      const txType = transactionType !== 'all' ? transactionType : undefined;
      const sDate = startDate && isValid(startDate) ? startDate.toISOString() : undefined;
      const eDate = endDate && isValid(endDate) ? endDate.toISOString() : undefined;
      const screenIdsParam = selectedSalesScreens.length > 0 ? selectedSalesScreens.map(s => s.id).join(',') : undefined;
      
      const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_COMPANY_TRANSACTIONS_PAGED({
        page: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm || undefined,
        sortColumn: sortColumn,
        sortDirection: sortDirection || 'desc',
        bankAccountId: bankAccId,
        transactionType: txType,
        startDate: sDate,
        endDate: eDate,
        screenIds: screenIdsParam,
      }));
      
      const result = response.data;
      
      if (result.success && result.data) {
        const txList: BankTransaction[] = result.data.map((data: any) => ({
          id: data.id.toString(),
          date: data.date || data.createdAt,
          amount: parseFloat(data.amount) || 0,
          bankAccountId: data.bankAccountId?.toString() || '',
          bankAccountName: data.bankAccount?.accountNumber || '',
          bankName: data.bankAccount?.bankName || '',
          bankSubAccountId: data.bankSubAccountId?.toString() || '',
          description: data.description || '',
          transactionType: data.transactionType || '',
          type: data.type || '',
          currency: data.currency || 'TRY',
          relatedAccountId: data.relatedAccountId,
          relatedAccountName: data.relatedAccountName,
          documentNo: data.documentNo,
          createdAt: data.createdAt || '',
          updatedAt: data.updatedAt || '',
          createdBy: data.createdByEmail || '',
          paymentMethod: data.paymentMethod,
          screenId: data.screenId,
          screenName: data.screenName || (data.screenId ? salesScreens.find(s => s.id === data.screenId)?.name : undefined),
          projectId: data.projectId ?? null,
          projectName: data.projectName ?? null,
          referenceType: data.referenceType ?? null,
          transferGroupId: data.transferGroupId ?? null
        }));

        setFilteredTransactions(txList);
        setTotalCount(result.totalCount || 0);
        setFilterTotals(Array.isArray(result.totals)
          ? result.totals.map((x: any) => ({ currency: x.currency || 'TRY', total: parseFloat(x.total) || 0 }))
          : null);
      } else {
        setFilteredTransactions([]);
        setTotalCount(0);
        setFilterTotals(null);
      }
    } catch (err: any) {
      console.error('❌ Banka işlemleri yüklenirken hata:', err);
      setError(err.response?.data?.message || err.message || t('bank.transactionReport.errors.loadTransactionsFailed'));
      setFilteredTransactions([]);
      setTotalCount(0);
      setFilterTotals(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, page, rowsPerPage, searchTerm, sortColumn, sortDirection, selectedBankAccount, transactionType, startDate, endDate, selectedSalesScreens, salesScreens]);

  // Server-side yükleme - debounce ile
  useEffect(() => {
    if (!companyId) return;
    const timer = setTimeout(() => {
      loadTransactions();
    }, 400);
    return () => clearTimeout(timer);
  }, [loadTransactions]);

  // Filtre değiştiğinde sayfa sıfırla
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedBankAccount, transactionType, selectedSalesScreens, startDate, endDate, sortColumn, sortDirection]);

  // Sayfalanmış işlemler (server tarafından sayfalanmış - mobil için doğrudan kullan)
  const paginatedTransactions = filteredTransactions;

  // GENEL TOPLAM (TRY): sunucu filtre toplamı varsa o (tüm sayfalar), yoksa açık sayfa — masaüstü ve mobil aynı değer
  const grandTotalIsFiltered = filterTotals !== null;
  const grandTotalTry = filterTotals !== null
    ? filterTotals.filter(x => x.currency === 'TRY').reduce((sum, x) => sum + x.total, 0)
    : sumBankTxSigned(filteredTransactions, 'TRY');
  const grandTotalScopeLabel = grandTotalIsFiltered
    ? t('bank.transactionReport.totalScopeFiltered')
    : t('bank.transactionReport.totalScopePage');

  // Banka hesaplarını yükle
  const loadBankAccounts = async (companyId: string) => {
    try {
      console.log('📋 Banka hesapları SQL\'den yükleniyor...');
      
      // SQL API'den banka hesaplarını yükle
      const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY);
      
      const result = response.data;
      const sqlAccounts = result.data || result;
      
      if (!sqlAccounts || sqlAccounts.length === 0) {
        console.log('Veritabanında banka hesabı bulunamadı');
        setBankAccounts([]);
        return;
      }
      
      const accountsList: BankAccount[] = sqlAccounts.map((data: any) => ({
        id: data.id.toString(),
        bankName: data.bankName || '',
        accountNumber: data.accountNumber || '',
        currency: data.currency || 'TRY'
      }));
      
      console.log(`✅ ${accountsList.length} banka hesabı yüklendi`);
      setBankAccounts(accountsList);
    } catch (error) {
      console.error('❌ Banka hesapları yüklenirken hata:', error);
      setError(t('bank.transactionReport.errors.loadBankAccountsFailed'));
    }
  };

  // Sayfa değişimi
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Para birimi sembolünü döndür
  const getCurrencySymbol = (currency: string): string => {
    switch(currency) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return currency;
    }
  };
  
  // İşlem tipine göre ikon ve renk döndür
  const getTransactionTypeInfo = (type: string) => {
    const upperType = type?.toUpperCase(); // Büyük harfe çevir
    switch (upperType) {
      case 'DEPOSIT':
      case 'INCOME': // income tipi de olabilir
        return {
          label: t('bank.transactionReport.types.deposit'),
          color: 'success',
          textColor: 'success.main'
        };
      case 'WITHDRAWAL':
      case 'WITHDRAW':
      case 'EXPENSE': // expense tipi de olabilir
        return {
          label: t('bank.transactionReport.types.withdraw'),
          color: 'error',
          textColor: 'error.main'
        };
      case 'CASH_TRANSFER':
        return {
          label: t('bank.transactionReport.types.transfer'),
          color: 'info',
          textColor: 'info.main'
        };
      case 'QUICK_SALE': // YENİ CASE
        return {
          label: t('bank.transactionReport.types.quickSale'),
          color: 'info', // Ya da 'success' olabilir
          textColor: 'info.main'
        };
      case 'COLLECTION': // Tahsilat için yeni case
        return {
          label: t('bank.transactionReport.types.collection'),
          color: 'success',
          textColor: 'success.main'
        };
      // Diğer spesifik tipler buraya eklenebilir
      default:
        // Eğer tip INCOME/EXPENSE ise ona göre ikon göster
        // if (transaction?.type?.toUpperCase() === 'INCOME') { ... } -> Bu bilgi burada yok
        // Şimdilik genel bir ikon ve etiket döndürelim
        return {
          label: type || t('bank.transactionReport.types.other'),
          color: 'default',
          textColor: 'text.primary'
        };
    }
  };
  
  // Ödeme yöntemini görüntüle
  const getPaymentMethodName = (paymentMethod?: string): string => {
    if (!paymentMethod) return '-';
    const keyMap: Record<string, string> = {
      cash: 'cash',
      credit_card: 'creditCard',
      bank_transfer: 'bankTransfer',
      check: 'check',
      pos: 'pos',
    };
    const key = keyMap[paymentMethod];
    return key ? t(`bank.transactionReport.paymentMethods.${key}`) : paymentMethod;
  };
  
  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('bank.excel.transactions.sheetName'));
      
      // Sütunları tanımla
      worksheet.columns = [
        { header: t('bank.excel.transactions.columns.dateTime'), key: 'dateTime', width: 20 },
        { header: t('bank.excel.transactions.columns.bank'), key: 'bankName', width: 25 },
        { header: t('bank.excel.transactions.columns.account'), key: 'accountName', width: 25 },
        { header: t('bank.excel.transactions.columns.transactionType'), key: 'transactionType', width: 15 },
        { header: t('bank.excel.transactions.columns.paymentMethod'), key: 'paymentMethod', width: 20 },
        { header: t('bank.excel.transactions.columns.salesScreen'), key: 'screenName', width: 20 },
        { header: t('bank.excel.transactions.columns.description'), key: 'description', width: 40 },
        { header: t('bank.excel.transactions.columns.documentNo'), key: 'documentNo', width: 15 },
        { header: t('bank.excel.transactions.columns.amount'), key: 'amount', width: 15 },
        { header: t('bank.excel.transactions.columns.currency'), key: 'currency', width: 10 },
      ];
      
      // Stil tanımlamaları
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Başlık stilini uygula
      worksheet.getRow(1).eachCell(cell => {
        cell.style = headerStyle;
      });
      
      // Filtrelenmiş verileri ekle
      filteredTransactions.forEach(transaction => {
        const typeInfo = getTransactionTypeInfo(transaction.transactionType);
        
        worksheet.addRow({
          dateTime: formatDateTime(transaction.date, transaction.transactionType),
          bankName: transaction.bankName,
          accountName: transaction.bankAccountName,
          transactionType: typeInfo.label,
          paymentMethod: getPaymentMethodName(transaction.paymentMethod),
          screenName: transaction.screenName || '-',
          description: transaction.description,
          documentNo: transaction.documentNo || '',
          // Yönlü tutar: çıkışlar eksi (ekrandaki GENEL TOPLAM ile aynı kural)
          amount: getBankTxSignedAmount(transaction).toLocaleString(locale),
          currency: getCurrencySymbol(transaction.currency),
        });
      });

      // GENEL TOPLAM satırı (para birimi bazında, dışa aktarılan satırlardan)
      const exportCurrencies = Array.from(new Set(filteredTransactions.map(tx => tx.currency || 'TRY')));
      exportCurrencies.forEach(cur => {
        const totalRow = worksheet.addRow({
          description: t('bank.transactionReport.totalLabel'),
          amount: sumBankTxSigned(filteredTransactions, cur).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          currency: getCurrencySymbol(cur),
        });
        totalRow.font = { bold: true };
      });

      // Zebrastripe (alternatif satır renklendirme)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.style = {
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: {
                top: { style: 'thin' as const },
                left: { style: 'thin' as const },
                bottom: { style: 'thin' as const },
                right: { style: 'thin' as const }
              }
            };
          });
        }
      });
      
      // Dosya adına tarih ekle
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${t('bank.excel.transactions.filePrefix')}_${currentDate}.xlsx`;
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      setError(null); // Varsa hata mesajını temizle
      console.log(`${filteredTransactions.length} adet banka hareketi Excel dosyasına aktarıldı.`);
      
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError(t('bank.transactionReport.errors.exportFailed'));
    } finally {
      setLoadingExport(false);
    }
  };

  // İşlem detay modalını açma fonksiyonu
  const handleOpenDetailModal = (transaction: BankTransaction) => {
    setSelectedTransaction(transaction);
    setIsDetailModalOpen(true);
    
    // Eğer gerekirse, burada işlem detaylarını getirmek için Firebase'den veri çekilebilir
    // setDetailModalLoading(true);
    // Veri çekme işlemi...
    // setDetailModalLoading(false);
  };

  // İşlem detay modalını kapatma fonksiyonu
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTransaction(null);
  };

  // ✅ 3 nokta menüsünü açma
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, transaction: BankTransaction) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTransactionForDelete(transaction);
  };

  // ✅ 3 nokta menüsünü kapatma
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // ✅ Silme dialogunu açma
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // ✅ Silme dialogunu kapatma
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedTransactionForDelete(null);
  };

  // ✅ İşlemi silme
  const handleDeleteTransaction = async () => {
    if (!selectedTransactionForDelete) return;

    setIsDeleting(true);
    try {
      console.log('🗑️ Banka işlemi siliniyor:', selectedTransactionForDelete.id);

      // Kullanıcı bilgisini al
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        throw new Error(t('bank.transactionReport.errors.sessionNotFound'));
      }

      // Kullanıcının email'ini al (Cloud Functions)
      const userData = await firebaseDataService.getUserData(uid);
      const userEmail = userData?.email || ownAuthSession.getCurrentEmail() || t('bank.transactionReport.deleteDialog.unknownUser');

      // ✅ MULTI-TENANT: companyId kontrolü
      if (!companyId) {
        throw new Error(t('bank.transactionReport.errors.companyNotFound'));
      }

      await accountingApi.post(
        ACCOUNTING_API.BANK_TRANSACTION_DELETE(parseInt(selectedTransactionForDelete.id)),
        {
          companyId: companyId,
          deletedBy: userEmail,
          reason: t('bank.transactionReport.deleteDialog.manualDeleteReason')
        }
      );

      console.log('✅ Banka işlemi başarıyla silindi');

      // İşlemleri server'dan yeniden yükle
      loadTransactions();

      setSnackbarMessage(t('bank.transactionReport.deleteDialog.deleteSuccess'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setSelectedTransactionForDelete(null);
    } catch (error: any) {
      console.error('❌ İşlem silinirken hata:', error);
      setSnackbarMessage(error.response?.data?.message || error.message || t('bank.transactionReport.deleteDialog.deleteFailed'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  // ✅ Snackbar'ı kapatma
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Filtreleri göster/gizle
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }
    }}>
      {/* Üst Başlık ve Gezinme - Responsive */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              handleGoBack();
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <ArrowBackIcon sx={{ mr: 0.5 }} fontSize="small" />
            {fromHub ? t('reportsHub.title', { ns: 'common2' }) : t('bank.title')}
          </Link>
          <Typography color="text.primary">{t('bank.transactionReport.title')}</Typography>
        </Breadcrumbs>
      </Box>
      
      {/* Header - Responsive */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: 2,
        mb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {t('bank.transactionReport.title')}
          </Typography>
          <GuideButton guideKey="finance.bankTransactionReport" />
        </Box>
        
        {isMobile ? (
          // Mobil: Dikey Stack
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <Button 
              fullWidth
              variant="outlined" 
              startIcon={showFilters ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={toggleFilters}
              size="small"
            >
              {showFilters ? t('bank.transactionReport.actions.hideFilters') : t('bank.transactionReport.actions.showFilters')}
            </Button>
            <Button 
              fullWidth
              variant="outlined" 
              startIcon={<ReceiptIcon />}
              onClick={() => setIsLogsModalOpen(true)}
              size="small"
            >
              {t('bank.transactionReport.actions.logs')}
            </Button>
            <Button 
              fullWidth
              variant="outlined" 
              startIcon={<FileDownloadIcon />}
              onClick={exportToExcel}
              disabled={loadingExport || loading || filteredTransactions.length === 0}
              size="small"
            >
              {loadingExport ? t('bank.actions.preparing') : t('bank.actions.downloadExcel')}
            </Button>
          </Box>
        ) : (
          // Desktop: Yan yana
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              startIcon={showFilters ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={toggleFilters}
            >
              {showFilters ? t('bank.transactionReport.actions.hideFilters') : t('bank.transactionReport.actions.showFilters')}
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<ReceiptIcon />}
              onClick={() => setIsLogsModalOpen(true)}
            >
              {t('bank.transactionReport.actions.logs')}
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<FileDownloadIcon />}
              onClick={exportToExcel}
              disabled={loadingExport || loading || filteredTransactions.length === 0}
            >
              {loadingExport ? t('bank.actions.preparing') : t('bank.actions.downloadExcel')}
            </Button>
          </Box>
        )}
      </Box>
      
      {/* Filtre Alanı - Responsive */}
      {showFilters && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
          {/* İşlem Ara filtresi */}
          <Grid item xs={12} md={2.3}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('bank.transactionReport.filters.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          {/* Banka Hesabı filtresi */}
          <Grid item xs={12} md={2.3}>
            <FormControl fullWidth size="small">
              <InputLabel id="bank-account-select-label">{t('bank.transactionReport.filters.bankAccount')}</InputLabel>
              <Select
                labelId="bank-account-select-label"
                value={selectedBankAccount}
                onChange={(e) => setSelectedBankAccount(e.target.value)}
                label={t('bank.transactionReport.filters.bankAccount')}
              >
                <MenuItem value="all">{t('bank.transactionReport.filters.allAccounts')}</MenuItem>
                {bankAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.bankName} - {account.accountNumber}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Satış Ekranı filtresi */}
          <Grid item xs={12} md={2.4}>
            <Autocomplete
              multiple
              size="small"
              options={salesScreens}
              getOptionLabel={(option) => option.name}
              value={selectedSalesScreens}
              onChange={(event, newValue) => {
                setSelectedSalesScreens(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('bank.transactionReport.filters.salesScreen')} placeholder={t('bank.transactionReport.filters.selectScreen')} />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    size="small"
                    label={option.name}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="transaction-type-select-label">{t('bank.transactionReport.filters.transactionType')}</InputLabel>
              <Select
                labelId="transaction-type-select-label"
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                label={t('bank.transactionReport.filters.transactionType')}
              >
                <MenuItem value="all">{t('bank.transactionReport.filters.allTransactions')}</MenuItem>
                <MenuItem value="deposit">{t('bank.transactionReport.types.deposit')}</MenuItem>
                <MenuItem value="withdrawal">{t('bank.transactionReport.types.withdraw')}</MenuItem>
                <MenuItem value="transfer">{t('bank.transactionReport.paymentMethods.bankTransfer')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
              <DateTimePicker
                label={t('bank.transactionReport.filters.startDate')}
                value={startDate}
                onChange={setStartDate}
                ampm={false}
                format="dd.MM.yyyy HH:mm"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
              <DateTimePicker
                label={t('bank.transactionReport.filters.endDate')}
                value={endDate}
                onChange={setEndDate}
                ampm={false}
                format="dd.MM.yyyy HH:mm"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          </Grid>
        </Paper>
      )}

      {/* Genel Toplam Kartı - Mobil (Üstte) */}
      {isMobile && !loading && filteredTransactions.length > 0 && (
        <Card sx={{ bgcolor: 'primary.lighter', border: '2px solid', borderColor: 'primary.main', mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t('bank.transactionReport.totalLabel')}
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {grandTotalTry.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {grandTotalScopeLabel}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Ana İçerik */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px' 
        }}>
          <CircularProgress />
        </Box>
      ) : filteredTransactions.length === 0 ? (
        <Paper elevation={3} sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          borderRadius: 2
        }}>
          <ReceiptIcon 
            sx={{ 
              fontSize: 80, 
              color: 'text.secondary',
              mb: 2
            }} 
          />
          
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
            {t('bank.transactionReport.empty.title')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" align="center">
            {searchTerm || selectedBankAccount !== 'all' || transactionType !== 'all' || startDate || endDate ? 
              t('bank.transactionReport.empty.filtered') :
              t('bank.transactionReport.empty.initial')}
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Desktop: AccountingDataTable */}
          {!isMobile && (
            <>
              <AccountingDataTable<BankTransaction>
                columns={transactionColumns}
                rows={filteredTransactions}
                getRowId={(row) => row.id}
                loading={loading}
                serverSidePagination={true}
                totalCount={totalCount}
                page={page}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[25, 50, 100, 200, 300, 400, 500, 1000, 1500, 2000]}
                onPageChange={(newPage, newRowsPerPage) => {
                  setPage(newPage);
                  setRowsPerPage(newRowsPerPage);
                }}
                onSort={(field, direction) => { setSortColumn(field); setSortDirection(direction); }}
                sortField={sortColumn}
                sortDirection={sortDirection}
                emptyMessage={t('bank.transactionReport.empty.initial')}
                emptyIcon={<ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
                ariaLabel={t('bank.transactionReport.tableAriaLabel')}
                columnResizeStorageKey="bank-transaction-report"
              />

              {/* GENEL TOPLAM */}
              {filteredTransactions.length > 0 && (
                <Paper elevation={1} sx={{ mt: 1, p: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('bank.transactionReport.totalLabel')}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {grandTotalTry.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {grandTotalScopeLabel}
                  </Typography>
                </Paper>
              )}
            </>
          )}
          
          {/* Mobile: Card View */}
          {isMobile && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {paginatedTransactions.map((transaction) => {
                const typeInfo = getTransactionTypeInfo(transaction.transactionType);
                const isIncome = getBankTxDirection(transaction) > 0;
                
                return (
                  <Card 
                    key={transaction.id}
                    sx={{ 
                      border: '1px solid',
                      borderColor: isIncome ? 'success.light' : 'error.light',
                      bgcolor: isIncome ? 'rgba(76, 175, 80, 0.04)' : 'rgba(244, 67, 54, 0.04)'
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      {/* Başlık - Banka ve İşlem Tipi */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <AccountBalanceIcon color="primary" sx={{ fontSize: 20 }} />
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {transaction.bankName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {transaction.bankAccountName}
                              {transaction.bankSubAccountId && ` (${t('bank.labels.subAccount')})`}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip 
                          label={typeInfo.label}
                          size="small"
                          color={typeInfo.color as any}
                        />
                      </Box>
                      
                      {/* Tarih ve Saat */}
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('bank.transactionReport.table.columns.date')}:
                        </Typography>
                        <Typography variant="body2">
                          {formatDateTime(transaction.date, transaction.transactionType)}
                        </Typography>
                      </Box>
                      
                      {/* Ödeme Yöntemi */}
                      {transaction.paymentMethod && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('bank.transactionReport.table.columns.paymentMethod')}:
                          </Typography>
                          <Typography variant="body2">
                            {getPaymentMethodName(transaction.paymentMethod)}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Satış Ekranı */}
                      {transaction.screenName && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('bank.transactionReport.table.columns.salesScreen')}:
                          </Typography>
                          <Typography variant="body2">
                            {transaction.screenName}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Açıklama */}
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {t('bank.transactionReport.table.columns.description')}:
                        </Typography>
                        <Typography variant="body2">
                          {transaction.description}
                        </Typography>
                        {transaction.documentNo && (
                          <Typography variant="caption" color="text.secondary">
                            {t('bank.transactionReport.table.columns.documentNo')}: {transaction.documentNo}
                          </Typography>
                        )}
                      </Box>
                      
                      {/* Tutar */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2, 
                        pt: 1.5, 
                        borderTop: '1px solid', 
                        borderColor: 'divider' 
                      }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('bank.transactionReport.table.columns.amount')}:
                        </Typography>
                        <Typography 
                          variant="h6" 
                          fontWeight="bold"
                          color={isIncome ? 'success.main' : 'error.main'}
                        >
                          {transaction.amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(transaction.currency)}
                        </Typography>
                      </Box>
                    </CardContent>
                    
                    <CardActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<RemoveRedEyeIcon fontSize="small" />}
                        onClick={() => handleOpenDetailModal(transaction)}
                      >
                        {t('bank.transactionReport.actions.detail')}
                      </Button>
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleMenuOpen(e, transaction)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                );
              })}
              
              {/* Mobil Sayfalama */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                mt: 2,
                pb: 2
              }}>
                <Typography variant="caption" color="text.secondary">
                  {t('bank.pagination.showing', { count: totalCount, from: page * rowsPerPage + 1, to: Math.min((page + 1) * rowsPerPage, totalCount) })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={page === 0}
                    onClick={(e) => handleChangePage(e, page - 1)}
                  >
                    {t('bank.pagination.previous')}
                  </Button>
                  <Typography variant="body2">
                    {t('bank.pagination.page', { page: page + 1, total: Math.ceil(totalCount / rowsPerPage) })}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={page >= Math.ceil(totalCount / rowsPerPage) - 1}
                    onClick={(e) => handleChangePage(e, page + 1)}
                  >
                    {t('bank.pagination.next')}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Detay Modalı */}
      <BankTransactionDetailModal
        open={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        transaction={selectedTransaction}
        loading={detailModalLoading}
      />

      {/* ✅ 3 Nokta Menüsü */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleDeleteDialogOpen}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('bank.actions.delete')}
        </MenuItem>
      </Menu>

      {/* ✅ Silme Onay Dialogu */}
      <Dialog
        open={deleteDialogOpen}
        onClose={!isDeleting ? handleDeleteDialogClose : undefined}
        maxWidth="sm"
        fullWidth
      >
        {(() => {
          // Entegrasyon işlemi kontrolü - transactionType veya açıklamada fatura numarası varsa
          const isIntegrationTransaction = 
            selectedTransactionForDelete?.transactionType === 'QUICK_SALE' || 
            selectedTransactionForDelete?.transactionType === 'SALES_INVOICE' ||
            selectedTransactionForDelete?.transactionType === 'PURCHASE_INVOICE' ||
            // Açıklamada fatura numarası pattern'i varsa (SF-XXXXXX veya AF-XXXXXX)
            /SF-\d{6}-\d{4}|AF-\d{6}-\d{4}/.test(selectedTransactionForDelete?.description || '');
          
          // Fatura numarasını açıklamadan çıkar
          const invoiceNumberMatch = selectedTransactionForDelete?.description?.match(/[SA]F-\d{6}-\d{4}/);
          const extractedInvoiceNumber = invoiceNumberMatch ? invoiceNumberMatch[0] : null;

          // Fatura dışı bağlantılı hareket (çek/senet, personel, kredi kartı, kart): buradan silinemez (backend de reddeder)
          const linkedSource = selectedTransactionForDelete ? getBankTxLinkedSource(selectedTransactionForDelete) : null;
          // Transfer/virman: iki bacak (karşı banka/kasa hareketi) birlikte silinir
          const isTransferLeg = selectedTransactionForDelete ? isBankTransferLeg(selectedTransactionForDelete) : false;

          return (
            <>
              <DialogTitle
                sx={{
                  bgcolor: isIntegrationTransaction ? 'warning.main' : 'error.main',
                  color: 'white'
                }}
              >
                {isIntegrationTransaction ? t('bank.transactionReport.deleteDialog.integrationTitle') : t('bank.transactionReport.deleteDialog.title')}
              </DialogTitle>
              <DialogContent>
                <DialogContentText component="div">
                  {/* Entegrasyon işlemleri için özel uyarı */}
                  {isIntegrationTransaction ? (
                    <>
                      <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {t('bank.transactionReport.deleteDialog.integrationCannotDelete')}
                        </Typography>
                      </Alert>
                      
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          {t('bank.transactionReport.deleteDialog.integrationDescription')}
                        </Typography>
                        {extractedInvoiceNumber && (
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                            {t('bank.transactionReport.deleteDialog.invoiceNumber', { number: extractedInvoiceNumber })}
                          </Typography>
                        )}
                      </Alert>

                      {selectedTransactionForDelete && (
                        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('bank.transactionReport.deleteDialog.bank')}:</strong> {selectedTransactionForDelete.bankName}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('bank.transactionReport.deleteDialog.account')}:</strong> {selectedTransactionForDelete.bankAccountName}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('bank.transactionReport.deleteDialog.amount')}:</strong> {selectedTransactionForDelete.amount.toLocaleString(locale)} {getCurrencySymbol(selectedTransactionForDelete.currency)}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('bank.transactionReport.deleteDialog.transactionType')}:</strong> {getTransactionTypeInfo(selectedTransactionForDelete.transactionType).label}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('bank.transactionReport.table.columns.description')}:</strong> {selectedTransactionForDelete.description}
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <>
                      {linkedSource ? (
                        <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
                          {t('bank.transactionReport.deleteDialog.linkedCannotDelete', {
                            source: t(`bank.transactionReport.deleteDialog.linkedSources.${linkedSource}`)
                          })}
                        </Alert>
                      ) : (
                        <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
                          {t('bank.transactionReport.deleteDialog.warning')}
                        </Alert>
                      )}
                      {!linkedSource && isTransferLeg && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {t('bank.transactionReport.deleteDialog.transferWarning')}
                          </Typography>
                        </Alert>
                      )}
                      {selectedTransactionForDelete && (
                        <Box>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('bank.transactionReport.deleteDialog.bank')}:</strong> {selectedTransactionForDelete.bankName}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('bank.transactionReport.deleteDialog.account')}:</strong> {selectedTransactionForDelete.bankAccountName}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('bank.transactionReport.deleteDialog.amount')}:</strong> {selectedTransactionForDelete.amount.toLocaleString(locale)} {getCurrencySymbol(selectedTransactionForDelete.currency)}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>{t('bank.transactionReport.table.columns.description')}:</strong> {selectedTransactionForDelete.description}
                          </Typography>
                          {!linkedSource && (
                            <Typography variant="body2" sx={{ mt: 2, color: 'error.main', fontWeight: 'bold' }}>
                              {t('bank.transactionReport.deleteDialog.confirm')}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDeleteDialogClose} disabled={isDeleting}>
                  {isIntegrationTransaction || linkedSource ? t('bank.common.close') : t('bank.common.cancel')}
                </Button>
                
                {/* Entegrasyon işlemleri için Fatura Yönetimi butonu */}
                {isIntegrationTransaction ? (
                  <Button
                    onClick={() => {
                      handleDeleteDialogClose();
                      navigate('/fatura/satis');
                    }}
                    variant="contained"
                    color="primary"
                    startIcon={<ReceiptIcon />}
                  >
                    {t('bank.transactionReport.deleteDialog.goInvoice')}
                  </Button>
                ) : linkedSource ? null : (
                  <Button
                    onClick={handleDeleteTransaction}
                    color="error"
                    variant="contained"
                    disabled={isDeleting}
                    startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                  >
                    {isDeleting ? t('bank.common.deleting') : t('bank.actions.delete')}
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ✅ Snackbar (Bildirim) */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* ✅ Silinen İşlem Logları Modalı */}
      <BankTransactionLogsModal
        open={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
        companyId={companyId}
      />
    </Box>
  );
};

export default BankTransactionReport; 